import { Box } from '@chakra-ui/react';
import React from 'react';

import { BorrowingBaseSummaryAccordion } from './BorrowingBaseSummaryAccordion';
import { BorrowingBaseSummaryAccordionRow } from './BorrowingBaseSummaryAccordionRow';
import { BorrowingBaseSummarySectionHeader } from './BorrowingBaseSummarySectionHeader';
import {
  BorrowingBaseSummaryInput,
  calculateTotalBorrowingBaseAmountAndEffectiveAdvanceRate,
  getColumnValuesForBorrowingBaseReportField,
} from './utils';

type BorrowingBaseSummaryFinalBorrowingBaseReportProps = {
  borrowingBaseReports: BorrowingBaseSummaryInput;
  showComparisonAndDifferenceColumns: boolean;
  showValueAndReferenceColumnsOnly: boolean;
};
export const BorrowingBaseSummaryFinalBorrowingBaseReport = ({
  borrowingBaseReports,
  showComparisonAndDifferenceColumns,
  showValueAndReferenceColumnsOnly,
}: BorrowingBaseSummaryFinalBorrowingBaseReportProps) => {
  // these intermediate calculation steps below are needed until totalBorrowingBaseAmount and effectiveAdvanceRate are migrated in bb migration phase 3
  const minuendBorrowingBaseNumbers = calculateTotalBorrowingBaseAmountAndEffectiveAdvanceRate(
    {
      borrowingBaseReport: borrowingBaseReports.minuend,
    },
  );

  const subtrahendBorrowingBaseNumbers = calculateTotalBorrowingBaseAmountAndEffectiveAdvanceRate(
    {
      borrowingBaseReport: borrowingBaseReports.subtrahend,
    },
  );

  const referenceBorrowingBaseNumbers = calculateTotalBorrowingBaseAmountAndEffectiveAdvanceRate(
    {
      borrowingBaseReport: borrowingBaseReports.reference,
    },
  );

  return (
    <Box w="100%">
      <BorrowingBaseSummarySectionHeader headerLabel="Borrowing Base Summary" />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Borrowing Base Amount"
            columnValues={{
              minuendValue:
                minuendBorrowingBaseNumbers?.totalBorrowingBaseAmount,
              subtrahendValue:
                subtrahendBorrowingBaseNumbers?.totalBorrowingBaseAmount,
              referenceValue:
                referenceBorrowingBaseNumbers?.totalBorrowingBaseAmount,
            }}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
            fieldName="totalBorrowingBaseAmount"
          />
        )}
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Assets"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'totalAssets',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
            fieldName="totalAssets"
          />
        )}
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Effective Advance Rate"
            columnValues={{
              minuendValue: minuendBorrowingBaseNumbers?.effectiveAdvanceRate,
              subtrahendValue:
                subtrahendBorrowingBaseNumbers?.effectiveAdvanceRate,
              referenceValue:
                referenceBorrowingBaseNumbers?.effectiveAdvanceRate,
            }}
            valueType="percentage"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
            fieldName="effectiveAdvanceRate"
          />
        )}
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Maximum Available Amount"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'maximumAvailableAmount',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
            fieldName="maximumAvailableAmount"
          />
        )}
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Borrowing Base Availability"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'borrowingBaseAvailability',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
            fieldName="borrowingBaseAvailability"
          />
        )}
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Borrowing Base Availability Rate"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'borrowingBaseAvailabilityRate',
            })}
            valueType="percentage"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
            fieldName="borrowingBaseAvailabilityRate"
          />
        )}
      />
    </Box>
  );
};
